import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';

export type LastPriceMarker = {
  y: number;
  price: number;
  isUp: boolean;
};

export type LastPriceCountdownOverlayUpdate = {
  visible: boolean;
  y: number;
  price: number;
  isUp: boolean;
  countdownText: string;
  debugSourcePerfTs?: number;
};

export interface LastPriceCountdownOverlayHandle {
  update: (next: LastPriceCountdownOverlayUpdate) => void;
}

export interface LastPriceCountdownOverlayProps {
  rightOffsetPx?: number;
}

export const LastPriceCountdownOverlay = forwardRef<LastPriceCountdownOverlayHandle, LastPriceCountdownOverlayProps>(({
  rightOffsetPx = 6,
}, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const priceRef = useRef<HTMLDivElement | null>(null);
  const countdownRef = useRef<HTMLDivElement | null>(null);
  const lastLatencyLogPerfRef = useRef<number>(0);
  const lastDebugLogPerfRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.display = 'none';
      containerRef.current.style.top = '0px';
    }
  }, []);

  useImperativeHandle(ref, () => ({
    update: (next: LastPriceCountdownOverlayUpdate) => {
      const el = containerRef.current;
      const badge = badgeRef.current;
      const priceEl = priceRef.current;
      const countdownEl = countdownRef.current;

      if (!el || !badge || !priceEl || !countdownEl) {
        return;
      }

      if (!next.visible) {
        el.style.display = 'none';
        return;
      }

      el.style.display = 'block';
      const parentHeight = el.parentElement?.clientHeight ?? 0;
      const rawY = Number.isFinite(next.y) ? next.y : 0;
      const clampedY = parentHeight > 0 ? Math.max(8, Math.min(rawY, parentHeight - 8)) : rawY;
      el.style.top = `${clampedY}px`;

      const debugEnabled = typeof window !== 'undefined' && Boolean((window as any).__overlayLatencyDebug);
      if (debugEnabled) {
        const now = performance.now();
        if (now - lastDebugLogPerfRef.current >= 1000) {
          lastDebugLogPerfRef.current = now;
          console.log('[LastPriceOverlay] overlay.update', {
            visible: next.visible,
            top: el.style.top,
            display: el.style.display,
          });
        }
      }

      if (debugEnabled && typeof next.debugSourcePerfTs === 'number') {
        const now = performance.now();
        if (now - lastLatencyLogPerfRef.current >= 1000) {
          lastLatencyLogPerfRef.current = now;
          const deltaMs = now - next.debugSourcePerfTs;
          console.log('[LastPriceOverlay] tick->overlay latency (ms):', Math.round(deltaMs));
        }
      }

      priceEl.textContent = next.price.toFixed(2);
      countdownEl.textContent = next.countdownText;

      badge.classList.toggle('bg-emerald-500', next.isUp);
      badge.classList.toggle('bg-rose-500', !next.isUp);
    },
  }), []);

  return (
    <div
      className="absolute pointer-events-none z-[999]"
      ref={containerRef}
      style={{
        right: rightOffsetPx,
        transform: 'translateY(-50%)',
      }}
    >
      <div
        ref={badgeRef}
        className="rounded-sm px-2 py-1 text-white shadow-lg border border-black/10 min-w-[64px] text-center bg-emerald-500"
      >
        <div ref={priceRef} className="text-xs font-semibold leading-none tabular-nums">0.00</div>
        <div ref={countdownRef} className="text-[10px] leading-none mt-0.5 tabular-nums opacity-95">00:00</div>
      </div>
    </div>
  );
});

LastPriceCountdownOverlay.displayName = 'LastPriceCountdownOverlay';
